import { array, bool, func, number, shape, string } from "prop-types"
import { Select, Checkbox } from "components/ui"
import { isBlank } from "utils/helpers"
import { css } from "@emotion/core"
import { colors } from "components/shared/admin_colors"
import MissingAttendeeInfoError from "./MissingAttendeeInfoError"

const RegistrantQuestion = ({ question, isEditing, onChange }) => {
  const handleChange = e => {
    return onChange(e, question.questionId)
  }

  const renderInput = () => {
    return (
      <input type="text" id={question.questionId} value={question.answer} onChange={handleChange} />
    )
  }

  const renderTextArea = () => {
    return <textarea id={question.questionId} value={question.answer} onChange={handleChange} />
  }

  const renderDropdown = () => {
    let options = [{ value: "", label: "Select" }]

    if (question.kind === "yesno") {
      options = [
        ...options,
        ...[
          { value: true, label: "Yes" },
          { value: false, label: "No" }
        ]
      ]
    } else {
      options = [
        ...options,
        ...question.choices.map(choice => ({
          value: choice,
          label: choice
        }))
      ]
    }

    const value =
      question.kind === "yesno"
        ? isBlank(question.answer)
          ? ""
          : question.answer === "true"
        : question.answer

    return (
      <Select
        id={`${question.questionId}`}
        name={`questionDropdown_${question.questionId}`}
        onChange={handleChange}
        options={options}
        value={value.toString()}
      />
    )
  }

  const renderCheckboxes = () => {
    return question.choices.map(choice => (
      <Checkbox
        key={choice}
        name={`question_${question.questionId}_choice_${choice}`}
        htmlFor={`question_${question.questionId}_choice_${choice}`}
        text={choice}
        value={choice}
        checked={question.answers.includes(choice)}
        labelClasses="mb-1 ws-n"
        onSelect={handleChange}
      />
    ))
  }

  const renderSingleCheckbox = () => {
    return (
      <Checkbox
        key={question.question}
        name={`choice_${question.question}`}
        htmlFor={`choice_${question.question}`}
        text={question.question}
        value={"true"}
        checked={question.answers.includes(true)}
        labelClasses="mb-1 ws-n"
        onSelect={handleChange}
      />
    )
  }

  const renderOption = () => {
    switch (question.kind) {
      case "text":
        return renderInput()
      case "paragraph":
        return renderTextArea()
      case "dropdown":
        return question.multiSelect ? renderCheckboxes() : renderDropdown()
      case "yesno":
        return renderDropdown()
      case "singlecheckbox":
        return renderSingleCheckbox()
    }
  }

  const questionClasses = ["reg-details__label mt-1@print"]

  if (question.publiclyRequired) {
    if (
      (question.kind == "dropdown" && question.multiSelect && isBlank(question.answers)) ||
      (question.kind != "singlecheckbox" && question.kind != "dropdown" && isBlank(question.answer))
    ) {
      questionClasses.push("reg-details__item-incomplete")
    }
  }

  const answerClasses = !question.header ? "mb-2" : ""

  const questionHeaderStyle = css`
    border-bottom: 1px solid ${colors.neutral_060};
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  `

  const questionHeaderHeadingStyle = css`
    font-weight: 700;
  `

  const singleCheckBoxAnswer =
    isBlank(question.answer) || question.answer === "Not checked" ? "No" : "Yes"

  const answer =
    isBlank(question.answer) && !question.header ? (
      <MissingAttendeeInfoError
        icon="general#exclamation-triangle"
        className="mt-4p"
        showErrorExplanation={question.publiclyRequired}
      />
    ) : (
      question.answer.toString()
    )

  const answerText = question.kind === "singlecheckbox" ? singleCheckBoxAnswer : answer

  return (
    <div className={`${!question.header && "d-f"}"`}>
      {(question.header || !isEditing) && (
        <div css={question.header && questionHeaderStyle}>
          <div
            className={questionClasses.join(" ")}
            css={question.header && questionHeaderHeadingStyle}
          >
            {question.question}
          </div>
          <div className="font-size-5 c-neutral-600">{question.description}</div>
          <div className={answerClasses}>{answerText}</div>
        </div>
      )}

      {!question.header && isEditing && (
        <>
          <div className="f-1 mb-2">
            <label htmlFor={question.questionId} className={questionClasses.join(" ")}>
              {question.kind != "singlecheckbox" && question.question}
            </label>
            {renderOption()}
          </div>
        </>
      )}
    </div>
  )
}

RegistrantQuestion.propTypes = {
  question: shape({
    text: string,
    id: number,
    required: bool,
    allowMultiple: bool,
    choices: array,
    description: string,
    kind: string
  }),
  answer: shape({
    answers: array
  }),
  isEditing: bool,
  onChange: func
}

export default RegistrantQuestion
